import React, { useState, useEffect, useContext, useRef } from "react";
import "emoji-mart/css/emoji-mart.css";
import { useParams } from "react-router-dom";
import { Picker } from "emoji-mart";
import clsx from "clsx";

import { makeStyles } from "@material-ui/core/styles";
import Paper from "@material-ui/core/Paper";
import InputBase from "@material-ui/core/InputBase";
import CircularProgress from "@material-ui/core/CircularProgress";
import { green } from "@material-ui/core/colors";
import IconButton from "@material-ui/core/IconButton";
import { Paperclip, MoreVertical, Smile, Send, XCircle, X, Mic, CheckCircle, PenLine } from "lucide-react";
import {
  Hidden,
  Menu,
  MenuItem,
  Tooltip,
} from "@material-ui/core";
import ClickAwayListener from "@material-ui/core/ClickAwayListener";

import { i18n } from "../../translate/i18n";
import api from "../../services/api";
import RecordingTimer from "./RecordingTimer";
import { ReplyMessageContext } from "../../context/ReplyingMessage/ReplyingMessageContext";
import { AuthContext } from "../../context/Auth/AuthContext";
import { useLocalStorage } from "../../hooks/useLocalStorage";
import { useThemeContext } from "../../context/DarkMode";
import toastError from "../../errors/toastError";

let Mp3Recorder = null;

const initRecorder = async () => {
  if (!Mp3Recorder) {
    try {
      const MicRecorder = (await import("mic-recorder-to-mp3")).default;
      Mp3Recorder = new MicRecorder({ bitRate: 128 });
    } catch (error) {
      console.error("Failed to initialize recorder:", error);
      return null;
    }
  }
  return Mp3Recorder;
};

const useStyles = makeStyles(theme => ({
  mainWrapper: {
    background: theme.palette.background.default,
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    borderTop: "1px solid rgba(0, 0, 0, 0.12)",
    [theme.breakpoints.down("sm")]: {
      position: "fixed",
      bottom: 0,
      width: "100%",
    },
  },

  mediaPreview: {
    display: "flex",
    flexWrap: "wrap",
    alignItems: "center",
    padding: "8px 10px",
    width: "100%",
    background: theme.palette.background.paper,
    borderTop: "1px solid rgba(0,0,0,0.08)",
    gap: 8,
    boxSizing: "border-box",
  },

  mediaThumb: {
    width: 48,
    height: 48,
    objectFit: "cover",
    borderRadius: 6,
    flexShrink: 0,
  },


  newMessageBox: {
    background: theme.palette.background.default,
    width: "100%",
    display: "flex",
    padding: "7px",
    alignItems: "center",
  },

  messageInputWrapper: {
    padding: 6,
    marginRight: 7,
    background: theme.palette.background.paper,
    display: "flex",
    borderRadius: 20,
    flex: 1,
    position: "relative",
  },

  messageInput: {
    paddingLeft: 10,
    flex: 1,
    border: "none",
  },

  sendMessageIcons: {
    color: "grey",
  },

  uploadInput: {
    display: "none",
  },


  emojiBox: {
    position: "absolute",
    bottom: 63,
    width: 40,
    borderTop: "1px solid #e8e8e8",
  },

  circleLoading: {
    color: green[500],
    opacity: "70%",
    position: "absolute",
    top: "20%",
    left: "50%",
    marginLeft: -12,
  },

  audioLoading: {
    color: green[500],
    opacity: "70%",
  },

  recorderWrapper: {
    display: "flex",
    alignItems: "center",
    alignContent: "middle",
  },

  cancelAudioIcon: {
    color: "red",
  },

  sendAudioIcon: {
    color: "green",
  },

  replyginMsgWrapper: {
    display: "flex",
    width: "100%",
    alignItems: "center",
    justifyContent: "center",
    paddingTop: 8,
    paddingLeft: 73,
    paddingRight: 7,
  },

  replyginMsgContainer: {
    flex: 1,
    marginRight: 5,
    overflowY: "hidden",
    backgroundColor: "rgba(0, 0, 0, 0.05)",
    borderRadius: "7.5px",
    display: "flex",
    position: "relative",
  },

  replyginMsgBody: {
    padding: 10,
    height: "auto",
    display: "block",
    whiteSpace: "pre-wrap",
    overflow: "hidden",
  },

  replyginContactMsgSideColor: {
    flex: "none",
    width: "4px",
    backgroundColor: "#35cd96",
  },

  replyginSelfMsgSideColor: {
    flex: "none",
    width: "4px",
    backgroundColor: "#6bcbef",
  },

  messageContactName: {
    display: "flex",
    color: "#6bcbef",
    fontWeight: 500,
  },
  messageQuickAnswersWrapper: {
    margin: 0,
    position: "absolute",
    bottom: "50px",
    background: theme.palette.background.paper,
    padding: "2px",
    border: `1px solid ${theme.palette.divider}`,
    left: 0,
    width: "100%",
    "& li": {
      listStyle: "none",
      "& a": {
        display: "block",
        padding: "8px",
        textOverflow: "ellipsis",
        overflow: "hidden",
        maxHeight: "32px",
        color: theme.palette.text.primary,
        "&:hover": {
          background: theme.palette.action.hover,
          cursor: "pointer",
        },
      },
    },
  },
}));

const MessageInput = ({ ticketStatus, droppedFiles = [], onDropHandled, onOptimisticSend }) => {
  const classes = useStyles();
  const { ticketId } = useParams();
  const { darkMode } = useThemeContext();

  const [medias, setMedias] = useState([]);
  const [inputMessage, setInputMessage] = useState("");
  const [showEmoji, setShowEmoji] = useState(false);
  const [loading, setLoading] = useState(false);
  const [recording, setRecording] = useState(false);
  const [quickAnswers, setQuickAnswer] = useState([]);
  const [typeBar, setTypeBar] = useState(false);
  const inputRef = useRef();
  const [anchorEl, setAnchorEl] = useState(null);
  const { setReplyingMessage, replyingMessage } =
    useContext(ReplyMessageContext);
  const { user } = useContext(AuthContext);

  const [signMessage, setSignMessage] = useLocalStorage("signOption", false);

  useEffect(() => {
    inputRef.current.focus();
  }, [replyingMessage]);

  useEffect(() => {
    inputRef.current.focus();
    return () => {
      setInputMessage("");
      setShowEmoji(false);
      setMedias([]);
      setReplyingMessage(null);
    };
  }, [ticketId, setReplyingMessage]);

  const handleChangeInput = e => {
    setInputMessage(e.target.value);
    handleLoadQuickAnswer(e.target.value);
  };

  const handleQuickAnswersClick = value => {
    setInputMessage(value);
    setTypeBar(false);
  };

  const handleAddEmoji = e => {
    let emoji = e.native;
    setInputMessage(prevState => prevState + emoji);
  };

  const handleChangeMedias = e => {
    if (!e.target.files) {
      return;
    }

    const selectedMedias = Array.from(e.target.files);
    setMedias(selectedMedias);
  };

  const handleInputPaste = e => {
    if (e.clipboardData.files[0]) {
      setMedias([e.clipboardData.files[0]]);
    }
  };

  useEffect(() => {
    if (droppedFiles.length > 0) {
      setMedias(prev => [...prev, ...droppedFiles]);
      onDropHandled();
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [droppedFiles]);

  const handleSendMessage = async () => {
    if (inputMessage.trim() === "" && medias.length === 0) return;
    const quotedMsgSnapshot = replyingMessage;
    setLoading(true);

    try {
      if (medias.length > 0) {
        if (onOptimisticSend) {
          const pending = medias.map((file, i) => ({
            id: `pending-${Date.now()}-${i}`,
            body: i === medias.length - 1 ? inputMessage.trim() : "",
            fromMe: true,
            mediaType: file.type.split("/")[0],
            previewUrl: URL.createObjectURL(file),
          }));
          onOptimisticSend(pending);
        }

        const formData = new FormData();
        formData.append("fromMe", true);
        medias.forEach(media => formData.append("medias", media));
        formData.append("body", inputMessage.trim());
        if (quotedMsgSnapshot) {
          formData.append("quotedMsg", JSON.stringify(quotedMsgSnapshot));
        }

        // Clear UI immediately
        setInputMessage("");
        setShowEmoji(false);
        setLoading(false);
        setMedias([]);
        setReplyingMessage(null);

        // POST fire-and-forget
        api.post(`/messages/${ticketId}`, formData).catch(toastError);
        return;
      } else {
        const body = signMessage
          ? `*${user?.name}:*\n${inputMessage.trim()}`
          : inputMessage.trim();
        await api.post(`/messages/${ticketId}`, {
          read: 1, fromMe: true, mediaUrl: "", body, quotedMsg: replyingMessage,
        });
      }
    } catch (err) {
      toastError(err);
    }

    setInputMessage("");
    setShowEmoji(false);
    setLoading(false);
    setMedias([]);
    setReplyingMessage(null);
  };

  const handleStartRecording = async () => {
    setLoading(true);
    try {
      const recorder = await initRecorder();
      if (!recorder) {
        throw new Error("Recorder not available");
      }
      await navigator.mediaDevices.getUserMedia({ audio: true });
      await recorder.start();
      setRecording(true);
      setLoading(false);
    } catch (err) {
      toastError(err);
      setLoading(false);
    }
  };

  const handleLoadQuickAnswer = async value => {
    if (value && value.indexOf("/") === 0) {
      try {
        const { data } = await api.get("/quickAnswers/", {
          params: { searchParam: inputMessage.substring(1) },
        });
        setQuickAnswer(data.quickAnswers);
        if (data.quickAnswers.length > 0) {
          setTypeBar(true);
        } else {
          setTypeBar(false);
        }
      } catch (err) {
        setTypeBar(false);
      }
    } else {
      setTypeBar(false);
    }
  };

  const handleUploadAudio = async () => {
    setLoading(true);
    try {
      const recorder = await initRecorder();
      if (!recorder) {
        throw new Error("Recorder not available");
      }
      const [, blob] = await recorder.stop().getMp3();
      if (blob.size < 10000) {
        setLoading(false);
        setRecording(false);
        return;
      }

      const formData = new FormData();
      const filename = `${new Date().getTime()}.mp3`;
      formData.append("medias", blob, filename);
      formData.append("body", filename);
      formData.append("fromMe", true);

      await api.post(`/messages/${ticketId}`, formData);
    } catch (err) {
      toastError(err);
    }

    setRecording(false);
    setLoading(false);
  };

  const handleCancelAudio = async () => {
    try {
      const recorder = await initRecorder();
      if (recorder) {
        await recorder.stop().getMp3();
      }
      setRecording(false);
    } catch (err) {
      toastError(err);
    }
  };

  const handleOpenMenuClick = event => {
    setAnchorEl(event.currentTarget);
  };

  const handleMenuItemClick = event => {
    setAnchorEl(null);
  };

  const renderReplyingMessage = message => {
    return (
      <div className={classes.replyginMsgWrapper}>
        <div className={classes.replyginMsgContainer}>
          <span
            className={clsx(classes.replyginContactMsgSideColor, {
              [classes.replyginSelfMsgSideColor]: !message.fromMe,
            })}
          ></span>
          <div className={classes.replyginMsgBody}>
            {!message.fromMe && (
              <span className={classes.messageContactName}>
                {message.contact?.name}
              </span>
            )}
            {message.mediaUrl ? (
              <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
                {message.mediaType === "image" && (
                  <img
                    src={message.mediaUrl}
                    alt=""
                    style={{ width: 48, height: 48, objectFit: "cover", borderRadius: 4, flexShrink: 0 }}
                  />
                )}
                <span style={{ opacity: 0.8, fontSize: "0.85em" }}>
                  {message.mediaType === "image"
                    ? message.body || "Photo"
                    : message.mediaType === "audio"
                    ? "Audio"
                    : message.mediaType === "video"
                    ? "Video"
                    : message.body || "Document"}
                </span>
              </div>
            ) : (
              message.body
            )}
          </div>
        </div>
        <IconButton
          aria-label="showRecorder"
          component="span"
          disabled={loading || ticketStatus !== "open"}
          onClick={() => setReplyingMessage(null)}
        >
          <X size={20} />
        </IconButton>
      </div>
    );
  };

  return (
      <Paper
        square
        elevation={0}
        className={classes.mainWrapper}
      >
        {replyingMessage && renderReplyingMessage(replyingMessage)}
        {medias.length > 0 && (
          <div className={classes.mediaPreview}>
            {medias.map((file, idx) => (
              <div key={idx} style={{ position: "relative", flexShrink: 0 }}>
                {file.type.startsWith("image/") ? (
                  <img
                    className={classes.mediaThumb}
                    src={URL.createObjectURL(file)}
                    alt={file.name}
                  />
                ) : (
                  <div className={classes.mediaThumb} style={{ background: "#ddd", display: "flex", alignItems: "center", justifyContent: "center" }}>
                    <Paperclip size={20} style={{ color: "#555" }} />
                  </div>
                )}
                <IconButton
                  size="small"
                  onClick={() => setMedias(prev => prev.filter((_, i) => i !== idx))}
                  style={{ position: "absolute", top: -6, right: -6, background: "rgba(0,0,0,0.55)", padding: 2 }}
                >
                  <X size={14} />
                </IconButton>
              </div>
            ))}
          </div>
        )}
        <div className={classes.newMessageBox}>
          <Hidden only={["sm", "xs"]}>
            <IconButton
              aria-label="emojiPicker"
              component="span"
              disabled={loading || recording || ticketStatus !== "open"}
              onClick={e => setShowEmoji(prevState => !prevState)}
            >
              <Smile size={20} />
            </IconButton>
            {showEmoji ? (
              <div className={classes.emojiBox}>
                <ClickAwayListener onClickAway={e => setShowEmoji(false)}>
                  <Picker
                    perLine={16}
                    showPreview={false}
                    showSkinTones={false}
                    onSelect={handleAddEmoji}
                    theme={darkMode ? 'dark' : 'light'}
                  />
                </ClickAwayListener>
              </div>
            ) : null}

            <input
              multiple
              type="file"
              id="upload-button"
              disabled={loading || recording || ticketStatus !== "open"}
              className={classes.uploadInput}
              onChange={handleChangeMedias}
            />
            <label htmlFor="upload-button">
              <IconButton
                aria-label="upload"
                component="span"
                disabled={loading || recording || ticketStatus !== "open"}
              >
                <Paperclip size={20} />
              </IconButton>
            </label>
            <Tooltip title={signMessage ? "Signature: on" : "Signature: off"} placement="top">
              <span>
                <IconButton
                  aria-label="toggleSignMessage"
                  component="span"
                  disabled={loading || recording || ticketStatus !== "open"}
                  onClick={() => setSignMessage(prev => !prev)}
                >
                  <PenLine
                    size={20}
                    style={{ color: signMessage ? "#25D366" : undefined }}
                  />
                </IconButton>
              </span>
            </Tooltip>
          </Hidden>
          <Hidden only={["md", "lg", "xl"]}>
            <IconButton
              aria-controls="simple-menu"
              aria-haspopup="true"
              onClick={handleOpenMenuClick}
            >
              <MoreVertical size={20} />
            </IconButton>
            <Menu
              id="simple-menu"
              keepMounted
              anchorEl={anchorEl}
              open={Boolean(anchorEl)}
              onClose={handleMenuItemClick}
            >
              <MenuItem onClick={handleMenuItemClick}>
                <IconButton
                  aria-label="emojiPicker"
                  component="span"
                  disabled={loading || recording || ticketStatus !== "open"}
                  onClick={e => setShowEmoji(prevState => !prevState)}
                >
                  <Smile size={20} />
                </IconButton>
              </MenuItem>
              <MenuItem onClick={handleMenuItemClick}>
                <input
                  multiple
                  type="file"
                  id="upload-button"
                  disabled={loading || recording || ticketStatus !== "open"}
                  className={classes.uploadInput}
                  onChange={handleChangeMedias}
                />
                <label htmlFor="upload-button">
                  <IconButton
                    aria-label="upload"
                    component="span"
                    disabled={loading || recording || ticketStatus !== "open"}
                  >
                    <Paperclip size={20} />
                  </IconButton>
                </label>
              </MenuItem>
              <MenuItem onClick={() => { setSignMessage(prev => !prev); handleMenuItemClick(); }}>
                <IconButton
                  aria-label="toggleSignMessage"
                  component="span"
                  disabled={loading || recording || ticketStatus !== "open"}
                >
                  <PenLine
                    size={20}
                    style={{ color: signMessage ? "#25D366" : undefined }}
                  />
                </IconButton>
              </MenuItem>
            </Menu>
          </Hidden>
          <div className={classes.messageInputWrapper}>
            <InputBase
              inputRef={input => {
                input && input.focus();
                input && (inputRef.current = input);
              }}
              className={classes.messageInput}
              placeholder="Type a message"
              multiline
              maxRows={5}
              value={inputMessage}
              onChange={handleChangeInput}
              disabled={recording || loading || ticketStatus !== "open"}
              onPaste={e => {
                ticketStatus === "open" && handleInputPaste(e);
              }}
              onKeyPress={e => {
                if (loading || e.shiftKey) return;
                else if (e.key === "Enter") {
                  handleSendMessage();
                }
              }}
            />
            {typeBar ? (
              <ul className={classes.messageQuickAnswersWrapper}>
                {quickAnswers.map((value, index) => {
                  return (
                    <li
                      className={classes.messageQuickAnswersWrapperItem}
                      key={index}
                    >
                      {/* eslint-disable-next-line jsx-a11y/anchor-is-valid */}
                      <a onClick={() => handleQuickAnswersClick(value.message)}>
                        {`${value.shortcut} - ${value.message}`}
                      </a>
                    </li>
                  );
                })}
              </ul>
            ) : (
              <div></div>
            )}
          </div>
          {(inputMessage || medias.length > 0) ? (
            <IconButton
              aria-label="sendMessage"
              component="span"
              onClick={handleSendMessage}
              disabled={loading}
            >
              <Send size={20} />
            </IconButton>
          ) : recording ? (
            <div className={classes.recorderWrapper}>
              <IconButton
                aria-label="cancelRecording"
                component="span"
                fontSize="large"
                disabled={loading}
                onClick={handleCancelAudio}
              >
                <XCircle size={20} style={{ color: "red" }} />
              </IconButton>
              {loading ? (
                <div>
                  <CircularProgress className={classes.audioLoading} />
                </div>
              ) : (
                <RecordingTimer />
              )}

              <IconButton
                aria-label="sendRecordedAudio"
                component="span"
                onClick={handleUploadAudio}
                disabled={loading}
              >
                <CheckCircle size={20} style={{ color: "green" }} />
              </IconButton>
            </div>
          ) : (
            <IconButton
              aria-label="showRecorder"
              component="span"
              disabled={loading || ticketStatus !== "open"}
              onClick={handleStartRecording}
            >
              <Mic size={20} />
            </IconButton>
          )}
        </div>
      </Paper>
    );
};

export default MessageInput;
